import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pager, ViewToggle, ConfirmModal } from '@/components/common';
import { TPL_CATEGORIES } from '@/data/mockTemplates';
import { useTemplates, useDuplicateTemplate, useDeleteTemplate } from '@/hooks/useTemplates';
import type { TemplateItem } from '@/api/types';
import type { BuilderState } from '@/types/survey.types';

interface CatalogSubTabProps {
  onOpenBuilder: (state: BuilderState) => void;
}

interface DeleteTarget { id: string; name: string }

const TPL_CAT_KEYS: Record<string, string> = {
  'Todos': 'admin.tplCatAll',
  'General': 'admin.tplCatGeneral',
  'Personalizada': 'admin.tplCatCustom',
};

export function CatalogSubTab({ onOpenBuilder }: CatalogSubTabProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<'cards' | 'list'>('cards');
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState<string>('Todos');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const { data: templates = [] } = useTemplates();
  const duplicateTemplate = useDuplicateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const filtered = templates.filter(c => {
    const matchName = (c.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
                      (c.description ?? '').toLowerCase().includes(search.toLowerCase());
    const matchCat = cat === 'Todos' || (c.category ?? '').toLowerCase() === cat.toLowerCase();
    return matchName && matchCat;
  });

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  function handleEdit(tpl: TemplateItem) {
    onOpenBuilder({
      mode: 'edit',
      source: 'templates',
      template: { id: tpl.id, name: tpl.name, description: tpl.description ?? '', category: tpl.category ?? '', mode: 'edit', startDate: tpl.createdAt, expirationDate: tpl.expiresAt },
    });
  }

  function handleUse(tpl: TemplateItem) {
    onOpenBuilder({
      mode: 'create',
      template: { id: tpl.id, name: tpl.name, description: tpl.description ?? '', category: tpl.category ?? '', mode: 'use', startDate: tpl.createdAt, expirationDate: tpl.expiresAt },
    });
  }

  function handleDuplicate(id: string) {
    duplicateTemplate.mutate(id);
  }

  function handleDeleteConfirm() {
    if (deleteTarget) deleteTemplate.mutate(deleteTarget.id);
    setDeleteTarget(null);
  }

  return (
    <>
      <div className="admin-header">
        <h2>{t('admin.templatesTitle')}</h2>
        <div className="tpl-toolbar">
          <ViewToggle view={view} onViewChange={setView} />
          <input
            className="tpl-search-input"
            type="text"
            placeholder={t('admin.tplSearchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <button className="btn btn-primary btn-small" onClick={() => onOpenBuilder({ mode: 'create', source: 'templates' })}>
            <i className="fas fa-plus" /> {t('admin.createTemplate')}
          </button>
        </div>
      </div>

      <div className="tpl-cat-filters">
        {TPL_CATEGORIES.map(c => (
          <button
            key={c}
            className={`tpl-cat-btn${cat === c ? ' active' : ''}`}
            onClick={() => { setCat(c); setPage(1); }}
          >
            {t(TPL_CAT_KEYS[c] ?? c)}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="tips-empty"><i className="fas fa-layer-group" /><p>{t('admin.tplNoResults')}</p></div>
      )}

      {view === 'cards' && filtered.length > 0 && (
        <>
        <div className="tpl-grid">
          {paged.map(tpl => (
            <div key={tpl.id} className="tpl-card">
              <button
                className="tpl-delete-corner-btn"
                title={t('common.delete')}
                onClick={() => setDeleteTarget({ id: tpl.id, name: tpl.name })}
              >
                <i className="fas fa-times" />
              </button>
              <div className="tpl-card-top">
                <div className="tpl-card-icon"><i className="fas fa-file-alt" /></div>
                <div className="tpl-card-info">
                  <div className="tpl-card-name">{tpl.name}</div>
                  <div className="tpl-card-desc">{tpl.description}</div>
                </div>
              </div>
              <div className="tpl-card-meta">
                <span className="tpl-meta-pill tpl-cat-pill">{t(TPL_CAT_KEYS[tpl.category ?? ''] ?? tpl.category ?? '')}</span>
                <span className="tpl-meta-item"><i className="fas fa-question-circle" /> {tpl.questionCount} {t('admin.tplQuestions')}</span>
              </div>
              <div className="tpl-card-footer">
                <span className="tpl-created-by"><i className="fas fa-user" /> {tpl.createdBy}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-small" title={t('common.edit')} onClick={() => handleEdit(tpl)}>
                    <i className="fas fa-edit" /> {t('common.edit')}
                  </button>
                  <button className="btn btn-secondary btn-small" title={t('admin.duplicate')} onClick={() => handleDuplicate(tpl.id)}>
                    <i className="fas fa-copy" />
                  </button>
                  <button className="btn btn-primary btn-small" onClick={() => handleUse(tpl)}>
                    <i className="fas fa-plus" /> {t('admin.tplUse')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="pager-cards">
          <Pager
            total={filtered.length}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={s => { setPageSize(s); setPage(1); }}
          />
        </div>
        </>
      )}

      {view === 'list' && filtered.length > 0 && (
        <div className="surveys-table-scroll" style={{ marginTop: 12 }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.colName')}</th>
                <th>{t('admin.tplCategory')}</th>
                <th>{t('admin.colQuestions')}</th>
                <th>{t('admin.colCreatedBy')}</th>
                <th>{t('admin.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0
                ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>{t('admin.tplNoResults')}</td></tr>
                : paged.map(tpl => (
                  <tr key={tpl.id}>
                    <td>
                      <strong>{tpl.name}</strong>
                      <div className="adm-table-subdesc">{tpl.description}</div>
                    </td>
                    <td><span className="tpl-meta-pill tpl-cat-pill">{tpl.category}</span></td>
                    <td><span className="question-count">{tpl.questionCount}</span></td>
                    <td>{tpl.createdBy}</td>
                    <td>
                      <div className="adm-actions">
                        <button className="btn-icon" title={t('common.edit')} onClick={() => handleEdit(tpl)}><i className="fas fa-edit" /></button>
                        <button className="btn-icon" title={t('admin.duplicate')} onClick={() => handleDuplicate(tpl.id)}><i className="fas fa-copy" /></button>
                        <button className="btn-icon success" title={t('admin.tplUse')} onClick={() => handleUse(tpl)}><i className="fas fa-plus" /></button>
                        <button className="btn-icon danger" title={t('common.delete')} onClick={() => setDeleteTarget({ id: tpl.id, name: tpl.name })}><i className="fas fa-trash" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
          <Pager
            total={filtered.length}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={s => { setPageSize(s); setPage(1); }}
          />
        </div>
      )}

      {deleteTarget !== null && (
        <ConfirmModal
          title={t('admin.tplDeleteTitle')}
          message={t('admin.tplDeleteDesc', { name: deleteTarget.name })}
          confirmText={t('common.delete')}
          cancelText={t('common.cancel')}
          type="danger"
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
